import { useEffect, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "configureStore";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { btnPrimaryCls } from "components/tw/classes";
import InvoiceSeriesForm from "containers/invoice-series-form/invoice-series-form";
import {
  ChangeLink,
  CatalogTable,
  SettingsCard,
  useScrollIntoViewOnOpen,
} from "containers/settings/ui";
import {
  fetchInvoiceSeriesList,
  setDestroy,
  InvoiceSeriesEntity,
} from "containers/invoice-series/invoice-series-container.slice";

/**
 * The sales invoice numbering registers.
 *
 * Deliberately a separate screen from Series: that list names files, contracts
 * and fuel reports and is written by the Finvalda sync, while these exist only
 * to number sales invoices. Mixing them would put registers a company must
 * never touch into the same picker as the ones it edits freely.
 *
 * Create one here, then choose it in Orders settings → sales invoice. Which
 * series invoices use is a setting, not a property of the series, so there is
 * nothing to activate here and nothing to retire.
 */
function InvoiceSeriesContainer() {
  const { collection, isLoading } = useSelector(
    (state: RootState) => state.invoiceSeriesReducer
  );
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  // null: the form is closed. `{}`: it is open for a new series.
  const [openForm, setOpenForm] = useState<{
    series?: InvoiceSeriesEntity;
  } | null>(null);
  const formRef = useScrollIntoViewOnOpen(openForm);

  const refresh = useCallback(() => {
    if (companyId) dispatch(fetchInvoiceSeriesList(companyId));
  }, [companyId, dispatch]);

  useEffect(() => {
    refresh();
    return () => {
      dispatch(setDestroy());
    };
  }, [refresh, dispatch]);

  if (!companyId) return null;

  const rows = collection.map((series: InvoiceSeriesEntity) => ({
    id: series.id,
    cells: {
      title: series.title,
      number: series.numberPreview,
      issued: series.issuedCount,
      status: series.numberingLocked && (
        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
          {t("invoice_series_in_use")}
        </span>
      ),
    },
  }));

  return (
    <SettingsCard
      title={t("invoice_series")}
      description={t("invoice_series_subtitle")}
      actions={
        <button
          type="button"
          className={btnPrimaryCls}
          onClick={() => setOpenForm({})}
        >
          {t("invoice_series_create")}
        </button>
      }
    >
      {openForm && (
        <div ref={formRef} className="mb-4">
          <InvoiceSeriesForm
            key={openForm.series?.id ?? "new"}
            series={openForm.series}
            onSaved={() => {
              refresh();
              setOpenForm(null);
            }}
            onCancel={() => setOpenForm(null)}
          />
        </div>
      )}
      {!isLoading && (
        <CatalogTable
          columns={[
            { key: "title", header: t("invoice_series_name") },
            {
              key: "number",
              header: t("settings_sales_series_current_number"),
            },
            { key: "issued", header: t("invoice_series_issued") },
            { key: "status", header: t("status") },
          ]}
          rows={rows}
          renderRowAction={(id) => (
            <ChangeLink
              onClick={() =>
                setOpenForm({
                  series: collection.find(
                    (series: InvoiceSeriesEntity) => series.id === id
                  ),
                })
              }
            />
          )}
          empty={t("invoice_series_none")}
        />
      )}
    </SettingsCard>
  );
}

export default InvoiceSeriesContainer;
